import React from 'react';
import { STATUS_COLORS, PRIORITY_COLORS, SEVERITY_COLORS } from '../../utils/constants';
import { Circle, Search, X } from 'lucide-react';

const XIcon = () => <X size={16} />;
const AlertIcon = () => <Circle size={18} />;
const SearchIcon = () => <Search size={15} />;

export const Badge = ({ value, type = 'status', variant, className = '' }) => {
  let color = className;
  if (variant) {
    const V = { red:'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', green:'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', yellow:'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', blue:'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', indigo:'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300', gray:'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' };
    color = V[variant] || className;
  } else if (type === 'status') color = STATUS_COLORS[value] || 'border border-slate-300 text-slate-500';
  else if (type === 'priority') color = PRIORITY_COLORS[value] || 'border border-slate-300 text-slate-500';
  else if (type === 'severity') color = SEVERITY_COLORS[value] || 'border border-slate-300 text-slate-500';
  return <span className={`badge ${color}`}>{value}</span>;
};

export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizes = { sm:'max-w-md', md:'max-w-xl', lg:'max-w-2xl', xl:'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${sizes[size]} rounded-xl shadow-2xl flex flex-col max-h-[90vh] animate-fade-in`} style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
        <div className="flex items-center justify-between p-5 border-b flex-shrink-0" style={{ borderColor:'var(--border)' }}>
          <h2 className="font-semibold text-base" style={{ color:'var(--text)' }}>{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}><XIcon /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>
  );
};

export const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message }) => (
  <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
    <div className="flex gap-3 mb-5"><AlertIcon /><p className="text-sm" style={{ color:'var(--text-secondary)' }}>{message}</p></div>
    <div className="flex gap-2 justify-end">
      <button onClick={onClose} className="btn-secondary">Cancel</button>
      <button onClick={onConfirm} className="btn-danger">Delete</button>
    </div>
  </Modal>
);

export const Spinner = ({ size = 'md', className = '' }) => {
  const s = size==='sm'?'w-4 h-4':size==='lg'?'w-8 h-8':'w-6 h-6';
  return <span className={`inline-flex items-center justify-center ${className}`}><svg className={`${s} animate-spin`} style={{ color:'#2E539F' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg></span>;
};

export const EmptyState = ({ icon, title, message, description, action }) => {
  const content = message || description;
  // `icon` may be a component to render, or an element already rendered.
  //
  // The old test was `typeof icon === 'function'`. A lucide-react icon is a
  // forwardRef OBJECT, not a function, so it failed that test and was passed
  // straight through as a child — which React rejects with
  // "Objects are not valid as a React child (found: object with keys
  // {$$typeof, render})". Any page passing a lucide icon here crashed.
  const isComponent = typeof icon === 'function'
    || (icon && typeof icon === 'object' && icon.$$typeof && !React.isValidElement(icon));
  const IconEl = isComponent
    ? React.createElement(icon, { size:28, style:{ color:'var(--text-secondary)' } })
    : icon;
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {IconEl && <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>{IconEl}</div>}
      <h3 className="font-medium mb-1" style={{ color:'var(--text)' }}>{title}</h3>
      {content && <p className="text-sm mb-4 max-w-xs" style={{ color:'var(--text-secondary)' }}>{content}</p>}
      {action}
    </div>
  );
};

export const SearchInput = ({ value, onChange, placeholder = 'Search...' }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}><SearchIcon /></span>
    <input value={value} onChange={onChange} placeholder={placeholder} className="input pl-9" />
  </div>
);

export const PageHeader = ({ title, subtitle, children, actions, icon }) => {
  const buttons = children || actions;
  return (
    <div className="flex items-start justify-between mb-6 gap-4">
      <div className="flex items-center gap-3">
        {icon && <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background:'rgba(46,83,159,.1)', color:'#2E539F' }}>{icon}</div>}
        <div><h1 className="page-title">{title}</h1>{subtitle && <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>{subtitle}</p>}</div>
      </div>
      {buttons && <div className="flex items-center gap-2 flex-shrink-0">{buttons}</div>}
    </div>
  );
};

export const StatCard = ({ label, value, icon: Icon, color = 'brand', trend }) => {
  const colors = { brand:{bg:'rgba(46,83,159,.12)',text:'#2E539F'}, green:{bg:'rgba(16,185,129,.12)',text:'#10b981'}, red:{bg:'rgba(239,68,68,.12)',text:'#ef4444'}, amber:{bg:'rgba(245,158,11,.12)',text:'#f59e0b'}, purple:{bg:'rgba(139,92,246,.12)',text:'#8b5cf6'}, blue:{bg:'rgba(59,130,246,.12)',text:'#3b82f6'} };
  const c = colors[color] || colors.brand;
  return (
    <div className="stat-card">
      <div className="flex items-center justify-between">
        <span className="stat-label">{label}</span>
        {Icon && <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background:c.bg, color:c.text }}><Icon size={16} /></div>}
      </div>
      <div><p className="text-3xl font-bold" style={{ color:'var(--text)' }}>{value}</p>{trend && <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>{trend}</p>}</div>
    </div>
  );
};

export const FormField = ({ label, required, children, error }) => (
  <div>
    <label className="label">{label}{required && <span className="text-red-400 ml-1">*</span>}</label>
    {children}
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

export const SelectField = ({ label, value, onChange, options, placeholder, children }) => (
  <div>
    {label && <label className="label">{label}</label>}
    <select value={value} onChange={onChange} className="select">
      {placeholder !== undefined && placeholder !== '' && <option value="">{placeholder}</option>}
      {options ? options.map(o => <option key={o.value} value={o.value}>{o.label}</option>) : children}
    </select>
  </div>
);
